import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { fetchSeatLimitsForSchool } from 'Store/schools/actions';
import * as reducers from 'Store/reducers';
import { getDateString } from '../../utilities';
import { getPlanByLicense } from 'Settings/pricing-options';

import Loading from 'Components/Loading';
import Button from 'Components/Button';
import SectionHeader from 'Components/SectionHeader';
import Link from 'Components/Link';
import ConnectedFormattedMessage from 'Containers/ConnectedFormattedMessage';

import { getStripeSubscription, toggleSubscriptionAutoRenew } from '../../api';
import Toggle from 'Components/Toggle';
import { ROUTES } from 'Settings/route-strings';
import PaymentMethodSettings from './PaymentMethodSettings';

const getFormattedDate = (date) =>
  getDateString(new Date(date), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const getPlanTitle = (plan) => {
  if (!plan) return null;
  const id = plan.licenseTerm === 'trial' ? 'trialTitleId' : 'titleId';
  return <ConnectedFormattedMessage id={plan[id]} />;
};

const InfoItem = ({ label, value }) => (
  <div style={{ display: 'flex', width: '100%' }}>
    <div style={{ flexBasis: 250, fontVariant: 'all-small-caps' }}>{label}</div>
    <div>{value}</div>
  </div>
);

function SubscriptionSettings(props) {
  const {
    authToken,
    activeSchool,
    schoolLicenses,
    fetchSeatLimitsForSchool,
  } = props;
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [postError, setPostError] = useState(null);
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const [isPaymentMethodFormVisible, setIsPaymentMethodFormVisible] = useState(
    false
  );

  const customerId = activeSchool && activeSchool.payment_user_id;
  const schoolId = activeSchool && activeSchool.id;

  const fetchSubscription = useCallback(() => {
    getStripeSubscription(authToken, schoolId).then((result) => {
      if (result.errorMsg) {
        setError(result.errorMsg);
      }
      setSubscription(result && result.subscription);
      setIsToggleLoading(false);
    });
  }, [authToken, schoolId]);

  useEffect(() => {
    if (!activeSchool) return;
    const { id, limits } = activeSchool;
    if (id && (!limits || !limits.teachers)) {
      fetchSeatLimitsForSchool(id, 'teachers');
    }
  }, [activeSchool, fetchSeatLimitsForSchool]);

  useEffect(() => {
    if (!activeSchool) return;
    const { id, limits } = activeSchool;

    if (id && (!limits || !limits.students)) {
      fetchSeatLimitsForSchool(id, 'students');
    }
  }, [activeSchool, fetchSeatLimitsForSchool]);

  useEffect(() => {
    if (authToken && schoolId) {
      fetchSubscription();
    }
  }, [fetchSubscription, authToken, schoolId]);

  if (!activeSchool || !schoolLicenses) return <Loading />;

  const license = schoolLicenses[0];
  const plan = license ? getPlanByLicense(license.license_type) : undefined;
  const { end_time: licenseEndDate } = license;
  const { cancel_at_period_end, current_period_end, next_payment_amount } =
    subscription || {};

  const planPrice = plan && `$${plan.computedPrice}`;

  const paymentDateString =
    subscription && getFormattedDate(current_period_end * 1000);

  const expiryDateString = getFormattedDate(licenseEndDate);

  const { brand, exp_month, exp_year, last4 } =
    (subscription && subscription.card_on_file) || {};

  const handleToggleAutoRenew = async () => {
    setIsToggleLoading(true);
    const { errorMsg } = await toggleSubscriptionAutoRenew(authToken, schoolId);
    if (errorMsg) {
      setPostError(errorMsg);
    }
    fetchSubscription();
  };

  return (
    <div>
      <SectionHeader
        title={<ConnectedFormattedMessage id="school_settings_page.title" />}
        type="static-modal"
      />
      <div>
        <div style={{ lineHeight: 2 }}>
          <div style={{ paddingBottom: '1em' }}>
            <h2>
              <ConnectedFormattedMessage id="subcription_settings.plan.title" />
            </h2>
            <InfoItem
              label={
                <ConnectedFormattedMessage id="subcription_settings.plan.type" />
              }
              value={getPlanTitle(plan)}
            />
            {plan.licenseTerm !== 'trial' && (
              <InfoItem
                label={
                  <ConnectedFormattedMessage id="subcription_settings.plan.price.label" />
                }
                value={
                  <ConnectedFormattedMessage
                    id="subcription_settings.plan.price.value"
                    values={{ price: planPrice }}
                  />
                }
              />
            )}
            <InfoItem
              label={
                <ConnectedFormattedMessage id="subcription_settings.plan.expires.label" />
              }
              value={expiryDateString}
            />
            <InfoItem
              label={
                <ConnectedFormattedMessage id="form.field.educatorSeats.label" />
              }
              value={
                activeSchool.limits &&
                activeSchool.limits.teachers && (
                  <ConnectedFormattedMessage
                    id="school_settings_page.seats_filled"
                    values={{
                      seats_filled: activeSchool.counts.teachers,
                      total: activeSchool.limits.teachers,
                    }}
                  />
                )
              }
            />
            <InfoItem
              label={
                <ConnectedFormattedMessage id="form.field.studentSeats.label" />
              }
              value={
                activeSchool.limits &&
                activeSchool.limits.teachers && (
                  <ConnectedFormattedMessage
                    id="school_settings_page.seats_filled"
                    values={{
                      seats_filled: activeSchool.counts.students,
                      total: activeSchool.limits.students,
                    }}
                  />
                )
              }
            />
          </div>

          {plan && plan.availableUpgrade && (
            <Button
              label={
                <ConnectedFormattedMessage id="school_settings_page.subscribe_button" />
              }
              route={ROUTES.SETTINGS_SUBSCRIPTION_PAYMENT}
              buttonType="primary"
              additionalClasses=" Button--fit-content"
            />
          )}

          {customerId && (
            <div>
              <h2>
                <ConnectedFormattedMessage id="subcription_settings.payment_header" />
              </h2>
              {postError && <div>{postError}</div>}
              {error && (
                <div>
                  <ConnectedFormattedMessage id="subcription_settings.error" />:{' '}
                  {error}
                </div>
              )}
              {!error && !subscription && (
                <Loading
                  style={{ minHeight: 'unset', height: 'auto' }}
                  type="component"
                />
              )}
              {!error && subscription && (
                <div>
                  <div>
                    <InfoItem
                      label={
                        <ConnectedFormattedMessage id="subcription_settings.auto_renew.title" />
                      }
                      value={
                        <div>
                          <div style={{ paddingTop: 4 }}>
                            <Toggle
                              isLoading={isToggleLoading}
                              checked={!cancel_at_period_end}
                              onChange={handleToggleAutoRenew}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: '0.8em',
                              opacity: 0.7,
                              position: 'relative',
                              top: -15,
                            }}
                          >
                            {cancel_at_period_end ? (
                              <ConnectedFormattedMessage
                                id="subcription_settings.auto_renew.license_end"
                                values={{ expiry_date: expiryDateString }}
                              />
                            ) : (
                              <ConnectedFormattedMessage
                                id="subcription_settings.auto_renew.card_charged"
                                values={{
                                  next_payment_amount,
                                  payment_date: paymentDateString,
                                }}
                              />
                            )}
                          </span>
                        </div>
                      }
                    />
                  </div>
                  <InfoItem
                    label={
                      <ConnectedFormattedMessage id="subcription_settings.auto_renew.payment_method" />
                    }
                    value={
                      <span>
                        {brand} {`ending in ${last4}`}
                        <br />
                        Exp. {exp_month}/{exp_year}
                        <br />
                        <Link
                          to={ROUTES.SETTINGS_PAYMENT}
                          type="button"
                          className="u-text-link"
                        >
                          <ConnectedFormattedMessage id="subcription_settings.auto_renew.change_payment_method" />
                        </Link>
                      </span>
                    }
                  />
                </div>
              )}

              {isPaymentMethodFormVisible && (
                <div>
                  <PaymentMethodSettings customerId={customerId} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  const activeSchool = reducers.getActiveSchool(state);
  return {
    authToken: reducers.getAuthToken(state),
    activeUser: reducers.getActiveUser(state),
    activeSchool,
    schoolLicenses: activeSchool
      ? reducers.getLicensesForSchool(state, activeSchool.id)
      : null,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    fetchSeatLimitsForSchool,
  })(SubscriptionSettings)
);
